import { EditOutlined, EyeOutlined, SearchOutlined, DeleteOutlined, TagOutlined } from "@ant-design/icons";
import {
  Badge,
  Collapse,
  Button,
  Input,
  message,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  Popconfirm,
  Select,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateSegments, SegmentTagsModal } from "src/features/Modal";
import { apiRequests } from "src/shared/api/api";
import "./Segments.css";
import dayjs from "dayjs";

const { Text } = Typography;
const { Option } = Select;

const sortByIdDesc = (arr) => {
  return [...arr].sort((a, b) => b.id - a.id);
};

const Segments = ({ websocket }) => {
  // --- moved here: states for tags modal ---
  const [tagsModalVisible, setTagsModalVisible] = useState(false);
  const [tagsSegmentId, setTagsSegmentId] = useState(null);

  useEffect(() => {
    if (!websocket) return;

    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Update last recalc timestamp
        setLastRecalcTimes((prev) => ({
          ...prev,
          [data.segment_id]: dayjs().format("DD.MM.YYYY, HH:mm"),
        }));

        // Track active recalcs
        if (data.event === "recalc_start") {
          setRecalculatingSegments((prev) => new Set([...prev, data.segment_id]));
        } else if (data.event === "recalc_finish") {
          setRecalculatingSegments((prev) => {
            const newSet = new Set(prev);
            newSet.delete(data.segment_id);
            return newSet;
          });
        }
      } catch (e) {
        console.warn("WS: невалидный JSON", event.data);
      }
    };

    // Attach event listener
    websocket.addEventListener("message", handleWebSocketMessage);

    // Cleanup on unmount
    return () => {
      websocket.removeEventListener("message", handleWebSocketMessage);
    };
  }, [websocket]);

  const [segments, setSegments] = useState([]);
  const [filteredSegments, setFilteredSegments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [editSegment, setEditSegment] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const [updatingIds, setUpdatingIds] = useState([]);

  // WebSocket
  const [lastRecalcTimes, setLastRecalcTimes] = useState({});
  const [recalculatingSegments, setRecalculatingSegments] = useState(new Set());

  // archivedFilter: null = all, false = active, true = archived
  const [archivedFilter, setArchivedFilter] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("is_archived");
    if (p === null) return false;
    return p === "true";
  });

  const getSegments = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (archivedFilter === true || archivedFilter === false) {
        params.is_archived = archivedFilter;
      }

      const res = await apiRequests.segments.getData(params);

      const initialTimestamps = {};
      (res.data || []).forEach((segment) => {
        initialTimestamps[segment.id] = dayjs(segment.updated_at).format("DD.MM.YYYY, HH:mm");
      });
      setLastRecalcTimes(initialTimestamps);

      const sorted = sortByIdDesc(res.data || []);
      setSegments(sorted);

      if (!searchText?.trim()) {
        setFilteredSegments(sorted);
      } else {
        const value = searchText.toLowerCase();
        const filtered = sorted.filter(
          (segment) =>
            segment.name?.toLowerCase().includes(value) ||
            String(segment.id).includes(value)
        );
        setFilteredSegments(sortByIdDesc(filtered));
      }
    } catch (err) {
      console.error("Ошибка загрузки сегментов:", err);
      message.error("Не удалось загрузить сегменты");
    } finally {
      setIsLoading(false);
    }
  }, [archivedFilter, searchText]);

  useEffect(() => {
    if (token) {
      apiRequests.setToken(token);
    }
    getSegments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSegments, token]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (token) {
      sp.set("token", token);
    }

    if (archivedFilter === null) {
      sp.delete("is_archived");
    } else {
      sp.set("is_archived", String(archivedFilter));
    }

    const searchString = sp.toString();
    navigate(`${location.pathname}${searchString ? `?${searchString}` : ""}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivedFilter, token]);

  const handleDeleteSegment = async (id) => {
    try {
      await apiRequests.segments.deleteData(id);
      message.success("Сегмент удалён");
      await getSegments();
    } catch (err) {
      console.error("Ошибка удаления сегмента:", err);
      message.error(err?.response?.data?.detail || "Не удалось удалить сегмент");
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredSegments(sortByIdDesc(segments));
      return;
    }

    const filtered = segments.filter(
      (segment) =>
        segment.name?.toLowerCase().includes(value.toLowerCase()) ||
        String(segment.id).includes(value)
    );

    setFilteredSegments(sortByIdDesc(filtered));
  };

  const handleToggleArchive = async (id, checked) => {
    try {
      setUpdatingIds((prev) => [...prev, id]);

      const originalSegment = segments.find((segment) => segment.id === id);

      const updatedSegment = {
        ...originalSegment,
        is_archived: !checked,
      };

      await apiRequests.segments.putData(id, updatedSegment);

      message.success(!checked ? "Сегмент архивирован" : "Сегмент активирован");

      await getSegments();
    } catch (error) {
      await getSegments();
      message.error("Ошибка изменения статуса сегмента");
      console.error("Archive toggle error:", error);
    } finally {
      setUpdatingIds((prev) => prev.filter((itemId) => itemId !== id));
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: "descend",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Статус",
      dataIndex: "is_archived",
      key: "is_archived",
      align: "center",
      render: (isArchived, record) => (
        <Badge dot={updatingIds.includes(record.id)} color="processing" status="processing">
          <Switch
            checked={!isArchived}
            onChange={(checked) => handleToggleArchive(record.id, checked)}
            loading={updatingIds.includes(record.id)}
            disabled={updatingIds.includes(record.id)}
          />
        </Badge>
      ),
    },
    {
      title: "Контрагенты",
      dataIndex: "contragent_stats",
      key: "contragent_stats",
      align: "center",
      render: (_, record) => {
        const total = record.contragents_count || 0;
        const entered = record.entered_contragents_count || 0;
        const exited = record.exited_contragents_count || 0;

        return (
          <Tooltip
            title={
              <div style={{ whiteSpace: "pre-line" }}>
                {`Всего контрагентов: ${total}`}
                {`\nЗашло: ${entered}`}
                {`\nВышло: ${exited}`}
              </div>
            }
          >
            <span>
              <strong style={{ color: "#000" }}>{total}</strong> /&nbsp;
              <span style={{ color: "green" }}>{entered}</span> /&nbsp;
              <span style={{ color: "red" }}>{exited}</span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Последний перерасчёт",
      dataIndex: "id",
      key: "last_recalc",
      render: (id) => {
        const isRecalculating = recalculatingSegments.has(id);
        const timestamp = lastRecalcTimes[id] || "-";

        return (
          <Space size="small" align="center">
            {isRecalculating ? (
              <>
                <div className="blinking-dot" />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Пересчёт...
                </Text>
              </>
            ) : (
              <>
                <span className="gray-dot" />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {timestamp}
                </Text>
              </>
            )}
          </Space>
        );
      },
    },
    {
      title: "Действия",
      key: "actions",
      width: 260,
      align: "center",
      render: (_, record) => (
        <Space>
          <Tooltip title="Просмотреть детали">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/segments_details/${record.id}/?token=${token}`)}
            />
          </Tooltip>

          <Tooltip title="Редактировать">
            <Button
              type="default"
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                setEditSegment(record);
                setEditModalVisible(true);
              }}
            />
          </Tooltip>

          {/* ===== кнопка открытия модалки тегов ===== */}
          <Tooltip title="Теги">
            <Button
              type="default"
              icon={<TagOutlined />}
              size="small"
              onClick={() => {
                setTagsSegmentId(record.id);
                setTagsModalVisible(true);
              }}
            />
          </Tooltip>

          <Tooltip title="Удалить">
            <Popconfirm
              title="Вы уверены, что хотите удалить сегмент?"
              onConfirm={() => handleDeleteSegment(record.id)}
              okText="Удалить"
              okButtonProps={{ danger: true }}
              cancelText="Отмена"
            >
              <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 10, display: "flex", gridGap: "8px", alignItems: "flex-start" }}>
        <div>
          <CreateSegments visible={isCreateModalVisible} setVisible={setIsCreateModalVisible} onSuccess={getSegments} />
        </div>
        <div style={{ flex: 1 }}>
          <Collapse ghost style={{ margin: "-5px 0" }}>
            <Collapse.Panel header="Фильтры поиска сегментов" key="1">
              <div style={{ display: "flex", gap: "15px", alignItems: "center", marginBottom: "15px" }}>
                <Input
                  placeholder="Поиск по названию или ID..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                  allowClear
                  style={{ width: 300 }}
                />
              </div>
            </Collapse.Panel>
          </Collapse>
        </div>
        <div>
          <Select
            value={archivedFilter === null ? "all" : archivedFilter === true ? "archived" : "active"}
            onChange={(val) => {
              if (val === "all") setArchivedFilter(null);
              else if (val === "active") setArchivedFilter(false);
              else setArchivedFilter(true);
            }}
            style={{ width: 160 }}
          >
            <Option value="all">Все</Option>
            <Option value="active">Активные</Option>
            <Option value="archived">Архивные</Option>
          </Select>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={filteredSegments.map((item) => ({ ...item, key: item.id }))}
        loading={isLoading}
        size="middle"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          showTotal: (total) => `Всего: ${total} сегментов`,
        }}
        locale={{ emptyText: "Нет доступных сегментов" }}
      />

      <CreateSegments
        visible={editModalVisible}
        setVisible={setEditModalVisible}
        onSuccess={() => {
          setEditModalVisible(false);
          setEditSegment(null);
          getSegments();
        }}
        initialValues={editSegment}
        editId={editSegment?.id}
        editMode={true}
      />

      <SegmentTagsModal
        visible={tagsModalVisible}
        onClose={() => { setTagsModalVisible(false); setTagsSegmentId(null); }}
        segmentId={tagsSegmentId}
      />
    </div>
  );
};

export default Segments;
